import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Icon } from './Icon';


interface IntegrationsPageProps {
  userId: string;
}

export const IntegrationsPage: React.FC<IntegrationsPageProps> = ({ userId }) => {
  const [sessionCount, setSessionCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchSessionCount = () => {
      supabase
        .from('interactions')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .then(({ count }) => setSessionCount(count ?? 0));
    };

    fetchSessionCount();

    const interactionsChannel = supabase
      .channel('integrations-interactions')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'interactions',
          filter: `user_id=eq.${userId}`
        },
        () => {
          fetchSessionCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(interactionsChannel);
    };
  }, [userId]);

  const extensionActive = sessionCount !== null && sessionCount > 0;

  return (
    <main className="main-content">
      <div className="main-container">
        <div className="intg-hero">
          <h2 className="intg-title">Integrations</h2>
          <p className="intg-sub">Salifly works as a layer on top of your existing tools — no data migration required.</p>
        </div>

        {/* Chrome Extension */}
        <div className="intg-section">
          <h3 className="intg-section-heading">Chrome Extension</h3>
          <div className="intg-card">
            <div className="intg-card-left">
              <div className="intg-icon-wrap">
                <Icon name="sparkle" size={20} />
              </div>
              <div>
                <div className="intg-card-name">Salifly Chrome Extension</div>
                <div className="intg-card-desc">
                  Captures screenshots and conversation threads from your active tab, then generates AI drafts in the side panel.
                </div>
              </div>
            </div>
            <div className="intg-card-right">
              <span className={`intg-status-badge ${extensionActive ? 'intg-status--active' : 'intg-status--inactive'}`}>
                {extensionActive ? 'Active' : 'Not detected'}
              </span>
              {!extensionActive && (
                <a
                  href="https://chromewebstore.google.com/detail/salifly-response-assistan/kjfhchpibmpfddkhihmjpjnmddbafaco"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary btn-sm"
                >
                  Install
                </a>
              )}
              {extensionActive && (
                <span className="intg-session-count">{sessionCount} session{sessionCount !== 1 ? 's' : ''} captured</span>
              )}
            </div>
          </div>
        </div>

        {/* Side Panel */}
        <div className="intg-section">
          <h3 className="intg-section-heading">Side Panel</h3>
          <div className="intg-card">
            <div className="intg-card-left">
              <div className="intg-icon-wrap">
                <Icon name="send" size={20} />
              </div>
              <div>
                <div className="intg-card-name">AI Draft Panel</div>
                <div className="intg-card-desc">
                  Opens alongside any CRM, Gmail, or LinkedIn tab. Shows lead context, deal state, and three ready-to-send draft variations.
                </div>
              </div>
            </div>
            <div className="intg-card-right">
              <span className={`intg-status-badge ${extensionActive ? 'intg-status--active' : 'intg-status--inactive'}`}>
                {extensionActive ? 'Available' : 'Install extension'}
              </span>
            </div>
          </div>
        </div>

        {/* Inline Suggestions */}
        <div className="intg-section">
          <h3 className="intg-section-heading">Inline Suggestions</h3>
          <div className="intg-card">
            <div className="intg-card-left">
              <div className="intg-icon-wrap">
                <Icon name="chat" size={20} />
              </div>
              <div>
                <div className="intg-card-name">Real-time Coaching</div>
                <div className="intg-card-desc">
                  Watches your reply fields on Gmail, LinkedIn, Outlook, and Salesforce. Flags weak phrasing with purple underlines and suggests improvements — powered by the same playbooks as the side panel.
                </div>
              </div>
            </div>
            <div className="intg-card-right">
              <span className={`intg-status-badge ${extensionActive ? 'intg-status--active' : 'intg-status--inactive'}`}>
                {extensionActive ? 'Active' : 'Install extension'}
              </span>
            </div>
          </div>
        </div>

      </div>
    </main>
  );
};
